import { ILocation } from '@rbi-ctg/frontend';
import { IRestaurant } from '@rbi-ctg/store';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import { coordinateDistance } from 'utils/rbi-common';
import { routes } from 'utils/routing';

const MINUTE_TO_MS = 60 * 1000;

export const isTimestampExpired = (drawerTimerMinutes: number): boolean => {
  const autoSelectTimestamp = syncStorage.getNumber(SyncStorageKey.AUTO_SELECT_STORE_TIMESTAMP);
  const timeoutInMS = drawerTimerMinutes * MINUTE_TO_MS;
  const timeDiff = Date.now() - (autoSelectTimestamp || 0);
  return !autoSelectTimestamp || timeDiff >= timeoutInMS;
};

/** Remaining cooldown ms; `0` means the drawer may show again. */
export const getCloserStoreCooldownRemainingMs = (drawerTimerMinutes: number): number => {
  if (drawerTimerMinutes <= 0) {
    return 0;
  }

  const autoSelectTimestamp = syncStorage.getNumber(SyncStorageKey.AUTO_SELECT_STORE_TIMESTAMP);
  if (!autoSelectTimestamp) {
    return 0;
  }

  return Math.max(0, autoSelectTimestamp + drawerTimerMinutes * MINUTE_TO_MS - Date.now());
};

const isExactOrNestedRoute = (pathname: string, route: string): boolean =>
  pathname === route || pathname.startsWith(`${route}/`);

/** Closer-store drawer: home, menu, offers, rewards, and my-code only. */
export const isEligibleRoute = (pathname: string): boolean => {
  return (
    pathname === routes.base ||
    pathname === routes.menu ||
    pathname.startsWith(`${routes.menu}/`) ||
    isExactOrNestedRoute(pathname, routes.offers) ||
    isExactOrNestedRoute(pathname, routes.rewards) ||
    pathname === routes.myCode
  );
};

/** Matches nearby-restaurant search rounding (~111m at the equator). */
export const COORDINATE_SEARCH_PRECISION = 3;

/** Recheck nearby stores only after the user moves at least this far. */
export const SIGNIFICANT_MOVE_MILES = 0.25;

/** Brief higher-accuracy watch after cold start / resume, then stop. */
export const COLD_START_LOCATION_WATCH_MS = 15_000;

/**
 * Suppress the closer-store prompt for this long after the guest's most recent order, so
 * it does not interrupt an order still being placed or picked up. Deliberately independent
 * of `TOP_ORDER_STATUS_BAR_TIME_OUT` — that flag controls how long the unrelated order-status
 * banner stays visible (safe default 120 minutes), not whether this feature should be gated.
 */
export const CLOSER_STORE_RECENT_ORDER_COOLDOWN_MINUTES = 30;

export const roundCoordinateForSearch = (value: number): number =>
  Number(value.toFixed(COORDINATE_SEARCH_PRECISION));

const getCoordinates = (
  location: ILocation | undefined
): { latitude: number; longitude: number } | null => {
  if (!location) {
    return null;
  }
  return {
    latitude: location.lat,
    longitude: location.lng,
  };
};

const getStoreCoordinates = (
  restaurant: IRestaurant | undefined | null
): { latitude: number; longitude: number } | null => {
  if (!restaurant?._id || !restaurant?.latitude || !restaurant?.longitude) {
    return null;
  }
  return {
    latitude: restaurant.latitude,
    longitude: restaurant.longitude,
  };
};

export const hasMovedSignificantly = (
  from: ILocation | null | undefined,
  to: ILocation | null | undefined,
  thresholdMiles: number = SIGNIFICANT_MOVE_MILES
): boolean => {
  const fromCoords = getCoordinates(from ?? undefined);
  const toCoords = getCoordinates(to ?? undefined);

  if (!fromCoords || !toCoords) {
    return true;
  }

  return coordinateDistance(fromCoords, toCoords) >= thresholdMiles;
};

/**
 * Returns true when the candidate should NOT replace the current store.
 * Incomplete coordinates fail closed (do not recommend).
 */
export const isNewStoreFurtherOrEqual = (
  userLocation: ILocation | undefined,
  newStoreData: IRestaurant | undefined | null,
  currentStoreData: IRestaurant | undefined | null
): boolean => {
  const userCoords = getCoordinates(userLocation);
  const newStoreCoords = getStoreCoordinates(newStoreData);
  const currentStoreCoords = getStoreCoordinates(currentStoreData);

  if (!userCoords || !newStoreCoords || !currentStoreCoords) {
    return true;
  }

  return (
    coordinateDistance(userCoords, newStoreCoords) >=
    coordinateDistance(userCoords, currentStoreCoords)
  );
};

export const sortRestaurantsByDistance = (
  userLocation: ILocation,
  restaurants: readonly IRestaurant[]
): IRestaurant[] => {
  const userCoords = getCoordinates(userLocation);
  if (!userCoords) {
    return [...restaurants];
  }

  return [...restaurants].sort((a, b) => {
    const aCoords = getStoreCoordinates(a);
    const bCoords = getStoreCoordinates(b);

    if (!aCoords && !bCoords) {
      return 0;
    }
    if (!aCoords) {
      return 1;
    }
    if (!bCoords) {
      return -1;
    }

    return coordinateDistance(userCoords, aCoords) - coordinateDistance(userCoords, bCoords);
  });
};

export const resolveCloserRestaurantCandidate = ({
  userLocation,
  nearbyRestaurants,
  activeStore,
  isAvailable,
}: {
  userLocation: ILocation;
  nearbyRestaurants: readonly IRestaurant[];
  activeStore: IRestaurant | null | undefined;
  isAvailable: (restaurant: IRestaurant) => boolean;
}): IRestaurant | null => {
  if (!nearbyRestaurants.length) {
    return null;
  }

  const closestAvailable = sortRestaurantsByDistance(userLocation, nearbyRestaurants).find(
    isAvailable
  );

  if (!closestAvailable || closestAvailable._id === activeStore?._id) {
    return null;
  }

  if (isNewStoreFurtherOrEqual(userLocation, closestAvailable, activeStore)) {
    return null;
  }

  return closestAvailable;
};
